// cURL commands for endpoint cards, the request builder and handoff steps.
// Credentials stay as {{secret:…}} placeholders, matching the skills'
// no-secrets rule. Nothing here sends a request.

/** Escape for a double-quoted shell string; `$` must not expand ($filter, $expand). */
const dq = (value) => String(value).replace(/[\\"$`]/g, (ch) => `\\${ch}`)

/** Single-quote a request body for -d. */
const sq = (value) => `'${String(value).replaceAll("'", "'\\''")}'`

const authLines = (secret = 'access_token') => [
  `-H "Authorization: Bearer {{secret:${secret}}}"`,
  '-H "Accept: application/json"',
]

/**
 * A curl command from its parts.
 * @param {{ method?: string, url: string, get?: [string, string, boolean?][], body?: string, secret?: string }} parts
 *   get: query pairs sent with curl -G; the third item urlencodes the value.
 */
export function curlCommand({ method = 'GET', url, get = [], body, secret }) {
  const upper = method.toUpperCase()
  const first = get.length
    ? `curl -G "${dq(url)}"`
    : upper === 'GET'
      ? `curl "${dq(url)}"`
      : `curl -X ${upper} "${dq(url)}"`
  const lines = [first]
  for (const [key, value, encode] of get) {
    lines.push(encode ? `--data-urlencode "${dq(`${key}=${value}`)}"` : `-d "${dq(`${key}=${value}`)}"`)
  }
  lines.push(...authLines(secret))
  if (body) {
    lines.push('-H "Content-Type: application/json"')
    lines.push(`-d ${sq(body)}`)
  }
  return lines.join(' \\\n  ')
}

const PLACEHOLDER = /\{([A-Za-z_][\w.]*)\}/g

/** Names of {Placeholders} in a path, in order, without duplicates. */
export function pathPlaceholders(path = '') {
  return [...new Set([...String(path).matchAll(PLACEHOLDER)].map((m) => m[1]))]
}

/** Replace {Name} with the value typed for it; untouched when empty. */
export function fillPath(path = '', values = {}) {
  return String(path).replace(PLACEHOLDER, (whole, name) => (values[name]?.trim() ? values[name].trim() : whole))
}

/** Query items from the new `query` list or the older required/optional lists. */
export function queryItems(input = {}) {
  if (Array.isArray(input.query)) return input.query.filter((q) => q?.name)
  const required = (input.required_query ?? []).map((name) => ({ name, required: true }))
  const optional = (input.optional_query ?? []).map((name) => ({ name, required: false }))
  return [...required, ...optional]
}

/**
 * REST request from an endpoint input plus what the user typed.
 * @param {object} input show_endpoint input
 * @param {{ params?: Record<string,string>, query?: Record<string,string>, expand?: string[] }} [values]
 */
export function buildCurl(input, values = {}) {
  const { method = 'GET', path = '', body, auth_secret: secret } = input ?? {}
  const url = fillPath(path, values.params)
  const pairs = []
  for (const item of queryItems(input)) {
    const typed = values.query?.[item.name]
    const fallback = item.required ? `{{${item.name}}}` : ''
    const value = typed !== undefined ? String(typed).trim() || fallback : item.required ? (item.example ?? fallback) : ''
    if (value) pairs.push(`${item.name}=${value}`)
  }
  if (values.expand?.length) pairs.push(`${input.expand_param || '$expand'}=${values.expand.join(',')}`)
  const full = pairs.length ? `${url}${url.includes('?') ? '&' : '?'}${pairs.join('&')}` : url
  return curlCommand({ method, url: full, body, secret })
}

/** OData date literal: v4 is unquoted ISO with Z; v3 wraps it in datetime'…'. */
export function odataDate(date, version = 'v4') {
  if (!date) return ''
  return version === 'v3' ? `datetime'${date}T00:00:00'` : `${date}T00:00:00Z`
}

/**
 * OData table query from the table details in the input plus the user's picks.
 * Returns the command and what the builder did, for the "Applied for you" list.
 * @param {object} input show_endpoint input with api "odata"
 * @param {{ company?: string, columns?: string[], since?: string, top?: number, ids?: string, excludeQuotes?: boolean }} values
 */
export function buildODataQuery(input, values = {}) {
  const version = input.odata_version ?? 'v4'
  const filters = []
  const applied = []
  if (input.company_column && values.company?.trim()) {
    filters.push(`${input.company_column} eq '${values.company.trim()}'`)
    applied.push(`Company filter on \`${input.company_column}\`.`)
  }
  if (input.date_column && values.since) {
    filters.push(`${input.date_column} gt ${odataDate(values.since, version)}`)
    applied.push(
      version === 'v3'
        ? `OData v3 date literal: \`datetime'…'\`.`
        : 'OData v4 date literal: unquoted `…T00:00:00Z`.',
    )
  }
  if (input.quote_filter && values.excludeQuotes) {
    filters.push(input.quote_filter)
    applied.push(`Quotes excluded with \`${input.quote_filter}\`.`)
  }
  const ids = String(values.ids ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  const maxIds = input.max_or_ids ?? 50
  if (input.key_column && ids.length) {
    filters.push(`(${ids.slice(0, maxIds).map((id) => `${input.key_column} eq ${id}`).join(' or ')})`)
    applied.push(
      `Ids sent as an \`or\` chain on \`${input.key_column}\`.${ids.length > maxIds ? ` Only the first ${maxIds} of ${ids.length} fit one call.` : ''}`,
    )
  }
  const allowed = input.columns ?? []
  const columns = (values.columns ?? []).filter((col) => !allowed.length || allowed.includes(col))
  const select = columns.length ? columns.join(',') : input.key_column
  const top = Number(values.top) || 200
  const get = []
  if (select) get.push(['$select', select, true])
  if (filters.length) get.push(['$filter', filters.join(' and '), true])
  get.push(['$top', String(top)], ['$skip', '0'])
  applied.push(`Paged with \`$top=${top}\` and \`$skip\`; loop until a page comes back short.`)
  return {
    curl: curlCommand({ method: 'GET', url: input.path, get, secret: input.auth_secret }),
    applied,
  }
}

/** A handoff step: cURL for a request, "METHOD path" plus the body for a payload. */
export function stepCode(step) {
  if (step.kind === 'payload') {
    const head = [step.method, step.path].filter(Boolean).join(' ')
    return [head, step.body].filter(Boolean).join('\n')
  }
  if (!step.path) return ''
  return curlCommand({ method: step.method ?? 'GET', url: step.path, body: step.body, secret: step.auth_secret })
}
